import {
  CheckSquare,
  DollarSign,
  GitBranch,
  Plus,
  Target,
  Trash2,
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { BillingPeriodSelect } from '@/shared/components/common/BillingPeriodSelect'
import { PricingModelSelect } from '@/shared/components/common/PricingModelSelect'
import { Button } from '@/shared/components/ui/button'
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from '@/shared/components/ui/drawer'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import type { EditableCharge } from '../../types/customerDetail.types'

interface AgentPricingDrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  selectedCharge: EditableCharge | null
  onUpdateCharge: (updatedCharge: EditableCharge) => void
  onDeleteCharge?: (chargeId: string) => void
}

export function AgentPricingDrawer({
  open,
  onOpenChange,
  selectedCharge,
  onUpdateCharge,
  onDeleteCharge,
}: AgentPricingDrawerProps) {
  // Local state for editing the charge
  const [editedCharge, setEditedCharge] = useState<EditableCharge | null>(null)
  const [tiers, setTiers] = useState<
    Array<{
      firstUnit: number
      lastUnit: number
      perUnitPrice: number
      flatFee: number
    }>
  >([])

  // Initialize local state when charge is selected
  useEffect(() => {
    if (selectedCharge && open) {
      setEditedCharge({ ...selectedCharge })

      // Initialize tiers if tiered pricing
      if (
        selectedCharge.pricingModel === 'tiered' ||
        selectedCharge.pricingModel === 'volume'
      ) {
        if (selectedCharge.tiers && selectedCharge.tiers.length > 0) {
          const mappedTiers = (
            selectedCharge.tiers as unknown as Record<string, unknown>[]
          ).map((tier: Record<string, unknown>) => ({
            firstUnit: Number(
              tier.firstUnit ||
                tier.min_units ||
                tier.first_unit ||
                tier.minUnits ||
                1
            ),
            lastUnit: Number(
              tier.lastUnit ||
                tier.max_units ||
                tier.last_unit ||
                tier.maxUnits ||
                Infinity
            ),
            perUnitPrice: parseFloat(
              String(
                tier.perUnitPrice ||
                  tier.unit_price ||
                  tier.per_unit_price ||
                  tier.unitPrice ||
                  '0'
              )
            ),
            flatFee: parseFloat(String(tier.flatFee || tier.flat_fee || '0')),
          }))
          setTiers(mappedTiers)
        } else {
          setTiers([
            {
              firstUnit: 1,
              lastUnit: 100,
              perUnitPrice: selectedCharge.unitPrice || 0,
              flatFee: 0,
            },
            {
              firstUnit: 101,
              lastUnit: Infinity,
              perUnitPrice: (selectedCharge.unitPrice || 0) * 0.8,
              flatFee: 0,
            },
          ])
        }
      } else {
        setTiers([])
      }
    }
  }, [selectedCharge, open])

  // Helper functions for tier management
  const handleAddTier = () => {
    const currentTiers = tiers
    setTiers([
      ...currentTiers,
      {
        firstUnit:
          currentTiers.length === 0
            ? 1
            : currentTiers[currentTiers.length - 1].lastUnit + 1,
        lastUnit: Infinity,
        perUnitPrice: 0,
        flatFee: 0,
      },
    ])
  }

  const handleRemoveTier = (tierIndex: number) => {
    const newTiers = tiers.filter((_, index) => index !== tierIndex)
    setTiers(newTiers)
  }

  const handleTierChange = (
    tierIndex: number,
    field: string,
    value: number
  ) => {
    const newTiers = tiers.map((tier, index) =>
      index === tierIndex ? { ...tier, [field]: value } : tier
    )
    setTiers(newTiers)
  }

  const handlePricingModelChange = (
    model: 'flat' | 'tiered' | 'volume' | 'per_unit'
  ) => {
    if (!editedCharge) return

    setEditedCharge({ ...editedCharge, pricingModel: model })

    if (model === 'tiered' || model === 'volume') {
      if (tiers.length === 0) {
        setTiers([
          {
            firstUnit: 1,
            lastUnit: 100,
            perUnitPrice: editedCharge.unitPrice || 0,
            flatFee: 0,
          },
          {
            firstUnit: 101,
            lastUnit: Infinity,
            perUnitPrice: (editedCharge.unitPrice || 0) * 0.8,
            flatFee: 0,
          },
        ])
      }
    }
  }

  const handleFieldUpdate = (
    field: keyof EditableCharge,
    value: EditableCharge[keyof EditableCharge]
  ) => {
    if (!editedCharge) return
    setEditedCharge({ ...editedCharge, [field]: value })
  }

  const handleSave = () => {
    if (!editedCharge) return

    // Prepare final charge with tiers if applicable
    const finalCharge = {
      ...editedCharge,
      tiers:
        editedCharge.pricingModel === 'tiered' ||
        editedCharge.pricingModel === 'volume'
          ? tiers
          : undefined,
      isEdited: true,
    }

    onUpdateCharge(finalCharge)
    onOpenChange(false)
  }

  const handleDelete = () => {
    if (selectedCharge && onDeleteCharge) {
      onDeleteCharge(selectedCharge.id)
      onOpenChange(false)
    }
  }

  const getSignalTypeIcon = (type?: string) => {
    switch (type?.toLowerCase()) {
      case 'task':
        return CheckSquare
      case 'workflow':
        return GitBranch
      case 'outcome':
        return Target
      default:
        return CheckSquare
    }
  }

  if (!selectedCharge || !editedCharge) return null

  const SignalTypeIcon = getSignalTypeIcon(editedCharge.kind)

  return (
    <Drawer open={open} onOpenChange={onOpenChange} direction='right'>
      <DrawerContent className='h-screen w-[50vw] min-w-[600px] max-w-[800px] ml-auto fixed inset-y-0 right-0 flex flex-col'>
        <DrawerHeader className='border-b flex-shrink-0 px-6 py-4'>
          <DrawerTitle>
            {selectedCharge.category === 'agent' ? (
              <>
                Edit Agent{' '}
                {(editedCharge.kind || 'Task').charAt(0).toUpperCase() +
                  (editedCharge.kind || 'task').slice(1)}
              </>
            ) : (
              <>Edit Feature</>
            )}
          </DrawerTitle>
          <p className='text-sm text-muted-foreground'>
            {selectedCharge.category === 'agent'
              ? `Configure pricing for this agent ${editedCharge.kind || 'task'}`
              : 'Configure pricing for this feature'}
          </p>
        </DrawerHeader>

        <div className='flex-1 overflow-y-auto px-6 py-4'>
          <div className='space-y-6'>
            {/* Basic Information */}
            <div className='space-y-4'>
              {selectedCharge.category === 'agent' && (
                <>
                  <div>
                    <Label className='text-sm font-medium'>Agent</Label>
                    <div className='mt-1'>
                      <span className='text-sm'>{editedCharge.agentName}</span>
                    </div>
                  </div>
                  <div>
                    <Label className='text-sm font-medium'>Signal</Label>
                    <div className='flex items-center gap-2 mt-1'>
                      <SignalTypeIcon className='w-4 h-4 text-muted-foreground' />
                      <span className='text-sm'>{editedCharge.signalName}</span>
                      <span className='text-xs text-muted-foreground capitalize'>
                        ({editedCharge.kind || 'task'})
                      </span>
                    </div>
                  </div>
                </>
              )}
              {selectedCharge.category !== 'agent' && (
                <div>
                  <Label className='text-sm font-medium'>Feature</Label>
                  <div className='mt-1'>
                    <span className='text-sm'>{editedCharge.name}</span>
                  </div>
                </div>
              )}
              {editedCharge.description && (
                <div>
                  <Label className='text-sm font-medium'>Description</Label>
                  <p className='text-sm text-muted-foreground mt-1'>
                    {editedCharge.description}
                  </p>
                </div>
              )}
            </div>

            {/* Pricing Configuration */}
            <div className='space-y-4'>
              <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                <div className='space-y-2'>
                  <Label>Billing Period</Label>
                  <BillingPeriodSelect
                    value={editedCharge.billingPeriod || 'monthly'}
                    onValueChange={value =>
                      handleFieldUpdate('billingPeriod', value)
                    }
                  />
                </div>
                <div className='space-y-2'>
                  <Label>Pricing Model</Label>
                  <PricingModelSelect
                    value={
                      ((editedCharge.pricingModel as string) ||
                        'flat') as import('@/shared/components/common/PricingModelSelect').PricingModel
                    }
                    onValueChange={value =>
                      handlePricingModelChange(value as never)
                    }
                  />
                </div>
              </div>

              {/* Unit Price - only for non-tiered/volume models */}
              {editedCharge.pricingModel !== 'tiered' &&
                editedCharge.pricingModel !== 'volume' && (
                  <div className='space-y-2'>
                    <Label>Unit Price</Label>
                    <div className='relative'>
                      <DollarSign className='absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground' />
                      <Input
                        type='number'
                        step='0.01'
                        value={editedCharge.unitPrice || 0}
                        onChange={e =>
                          handleFieldUpdate(
                            'unitPrice',
                            parseFloat(e.target.value) || 0
                          )
                        }
                        placeholder='0.00'
                        className='pl-10'
                      />
                    </div>
                  </div>
                )}

              {/* Additional Configuration */}
              <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                <div className='space-y-2'>
                  <Label>Included Units</Label>
                  <Input
                    type='number'
                    value={editedCharge.includedUnits || 0}
                    onChange={e =>
                      handleFieldUpdate(
                        'includedUnits',
                        parseInt(e.target.value) || 0
                      )
                    }
                    placeholder='0'
                  />
                </div>
                <div className='space-y-2'>
                  <Label>Minimum Commitment</Label>
                  <Input
                    type='number'
                    value={editedCharge.minCommitment || 0}
                    onChange={e =>
                      handleFieldUpdate(
                        'minCommitment',
                        parseInt(e.target.value) || 0
                      )
                    }
                    placeholder='0'
                  />
                </div>
              </div>

              {/* Tiered pricing configuration */}
              {(editedCharge.pricingModel === 'tiered' ||
                editedCharge.pricingModel === 'volume') && (
                <div className='space-y-4'>
                  <div className='flex items-center justify-between'>
                    <Label className='text-base'>Pricing Tiers</Label>
                    <Button
                      type='button'
                      onClick={handleAddTier}
                      size='sm'
                      variant='outline'
                    >
                      <Plus className='w-4 h-4 mr-2' />
                      Add Tier
                    </Button>
                  </div>

                  {/* Tier headers */}
                  <div className='grid grid-cols-12 gap-2 mb-2 text-sm font-medium text-muted-foreground'>
                    <div className='col-span-2'>First unit</div>
                    <div className='col-span-2'>Last unit</div>
                    <div className='col-span-3'>Per unit price</div>
                    <div className='col-span-3'>Flat fee</div>
                    <div className='col-span-2'></div>
                  </div>

                  {tiers.map((tier, tierIndex) => (
                    <div
                      // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
                      key={tierIndex}
                      className='grid grid-cols-12 gap-2 mb-2 items-center'
                    >
                      <div className='col-span-2'>
                        <Input
                          type='number'
                          value={tier.firstUnit}
                          onChange={e =>
                            handleTierChange(
                              tierIndex,
                              'firstUnit',
                              parseInt(e.target.value) || 1
                            )
                          }
                          placeholder='1'
                          className='text-sm'
                        />
                      </div>
                      <div className='col-span-2'>
                        <Input
                          type='number'
                          value={
                            tier.lastUnit === Infinity ? '' : tier.lastUnit
                          }
                          onChange={e =>
                            handleTierChange(
                              tierIndex,
                              'lastUnit',
                              parseInt(e.target.value) || Infinity
                            )
                          }
                          placeholder={
                            tierIndex === tiers.length - 1 ? '∞' : '100'
                          }
                          className='text-sm'
                        />
                      </div>
                      <div className='col-span-3'>
                        <div className='relative'>
                          <DollarSign className='absolute left-2 top-1/2 transform -translate-y-1/2 w-3 h-3 text-muted-foreground' />
                          <Input
                            type='number'
                            step='0.01'
                            value={tier.perUnitPrice}
                            onChange={e =>
                              handleTierChange(
                                tierIndex,
                                'perUnitPrice',
                                parseFloat(e.target.value) || 0
                              )
                            }
                            placeholder='0.00'
                            className='pl-6 text-sm'
                          />
                        </div>
                      </div>
                      <div className='col-span-3'>
                        <div className='relative'>
                          <DollarSign className='absolute left-2 top-1/2 transform -translate-y-1/2 w-3 h-3 text-muted-foreground' />
                          <Input
                            type='number'
                            step='0.01'
                            value={tier.flatFee}
                            onChange={e =>
                              handleTierChange(
                                tierIndex,
                                'flatFee',
                                parseFloat(e.target.value) || 0
                              )
                            }
                            placeholder='0'
                            className='pl-6 text-sm'
                          />
                        </div>
                      </div>
                      <div className='col-span-2 flex justify-end'>
                        <Button
                          type='button'
                          variant='outline'
                          size='sm'
                          onClick={() => handleRemoveTier(tierIndex)}
                        >
                          <Trash2 className='w-4 h-4' />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Fixed Footer with Action Buttons */}
        <div className='border-t bg-background px-6 py-4 flex-shrink-0'>
          <div className='flex justify-between'>
            {onDeleteCharge && (
              <Button variant='destructive' onClick={handleDelete}>
                <Trash2 className='w-4 h-4 mr-2' />
                Delete{' '}
                {selectedCharge.category === 'agent'
                  ? (editedCharge.kind || 'Task').charAt(0).toUpperCase() +
                    (editedCharge.kind || 'task').slice(1)
                  : 'Feature'}
              </Button>
            )}
            <div className='flex gap-2 ml-auto'>
              <Button variant='outline' onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button onClick={handleSave}>Save Changes</Button>
            </div>
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  )
}
